/**
 * SecretFieldEditor Component - Editable view of secret field
 */

import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames/bind';
import { SecretFieldEditorProps, SecretFieldType, MAX_SECRET_CONTENT_LENGTH } from './types';
import { validateSecretField, copyToClipboard } from './secretFieldUtils';
import { useDecryptedContent } from './useDecryptedContent';
import { useMasterPasswordStore } from '@/store/masterPasswordStore';
import { resolveNoteKey } from '@/services/NoteKeyResolver';
import { getCurrentNoteId } from './secretFieldContext';
import styles from './SecretField.module.scss';

const cx = classNames.bind(styles);

const SecretFieldEditor: React.FC<SecretFieldEditorProps> = ({
  nodeKey,
  label,
  content,
  type,
  onUpdate,
  onTypeChange,
  onDelete,
  onCommit,
}) => {
  // Decrypt content for display — the node may hold ciphertext (`enc:…`)
  const { plaintext, isEncrypted, decryptionFailed } = useDecryptedContent(content);

  // When content is encrypted and key is unavailable, the field is "locked"
  const isLocked = isEncrypted && plaintext === null;

  const displayContent = plaintext ?? '';

  const isConfigured    = useMasterPasswordStore((s) => s.isConfigured);
  const openModal       = useMasterPasswordStore((s) => s.openModal);
  const openUnlockModal = useMasterPasswordStore((s) => s.openUnlockModal);

  const tryResolveAfterUnlock = () => {
    const noteId = getCurrentNoteId();
    if (noteId) void resolveNoteKey(noteId);
  };

  const handleUnlock = () => {
    if (!isConfigured) {
      openModal(tryResolveAfterUnlock);
    } else {
      openUnlockModal(tryResolveAfterUnlock);
    }
  };

  const [localLabel, setLocalLabel] = useState(label);
  const [localContent, setLocalContent] = useState(displayContent);
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [showLabel, setShowLabel] = useState(!!label); // Show label if it exists
  const [error, setError] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(displayContent.length);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);

  const labelInputRef = useRef<HTMLInputElement>(null);
  const contentTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync with props (use decrypted plaintext, not raw content)
  useEffect(() => {
    setLocalLabel(label);
    setLocalContent(displayContent);
    setCharCount(displayContent.length);
  }, [label, displayContent]);

  // Dynamically adjust label input width based on content
  useEffect(() => {
    if (labelInputRef.current) {
      const input = labelInputRef.current;
      // Create a temporary span to measure text width
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.position = 'absolute';
      span.style.whiteSpace = 'pre';
      span.style.font = window.getComputedStyle(input).font;
      span.style.fontSize = window.getComputedStyle(input).fontSize;
      span.style.fontWeight = window.getComputedStyle(input).fontWeight;
      span.textContent = localLabel || input.placeholder;
      document.body.appendChild(span);

      const textWidth = span.offsetWidth;
      document.body.removeChild(span);

      // Add padding (0.375rem + 0.5rem = 0.875rem on each side, roughly 14px per side = 28px total)
      const padding = 28;
      const minWidth = 20;
      const newWidth = Math.max(minWidth, textWidth + padding);

      input.style.width = `${newWidth}px`;
    }
  }, [localLabel]);

  // Dynamically adjust textarea width and height based on content
  useEffect(() => {
    if (contentTextareaRef.current && isEditingContent) {
      const textarea = contentTextareaRef.current;

      // Reset height to get accurate scrollHeight
      textarea.style.height = 'auto';

      // Set height based on content, but cap at 200px to allow scrolling
      const scrollHeight = textarea.scrollHeight;
      const maxHeight = 200;
      const newHeight = Math.min(scrollHeight, maxHeight);
      textarea.style.height = `${newHeight}px`;

      // Check if content exceeds max height and needs scrolling
      setIsScrollable(scrollHeight > maxHeight);

      // Calculate width based on content
      const lines = localContent.split('\n');
      const longestLine = lines.reduce((max, line) => Math.max(max, line.length), 0);

      // Create temporary span to measure width
      const span = document.createElement('span');
      span.style.visibility = 'hidden';
      span.style.position = 'absolute';
      span.style.whiteSpace = 'pre';
      span.style.font = window.getComputedStyle(textarea).font;
      span.style.fontSize = window.getComputedStyle(textarea).fontSize;
      span.style.fontFamily = window.getComputedStyle(textarea).fontFamily;
      span.textContent = lines.find(line => line.length === longestLine) || localContent || textarea.placeholder;
      document.body.appendChild(span);

      const textWidth = span.offsetWidth;
      document.body.removeChild(span);

      // Add padding (roughly 16px total)
      const padding = 16;
      const minWidth = 120;
      const maxWidth = 300;
      const newWidth = Math.max(minWidth, Math.min(maxWidth, textWidth + padding));

      textarea.style.width = `${newWidth}px`;
    }
  }, [localContent, isEditingContent]);

  // Focus label input on mount if empty
  useEffect(() => {
    if (!localLabel && labelInputRef.current) {
      labelInputRef.current.focus();
    }
  }, []);

  const handleLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLabel = e.target.value;
    setLocalLabel(newLabel);

    // Validate (label is now optional)
    const validation = validateSecretField(newLabel, localContent);
    setError(validation.valid ? null : validation.error || null);

    if (validation.valid) {
      onUpdate(newLabel, localContent);
    }
  };

  const handleLabelBlur = () => {
    // If label is empty or only spaces, remove it
    if (!localLabel.trim()) {
      setLocalLabel('');
      setShowLabel(false);
      onUpdate('', localContent);
    }
    // Notify parent that editing is complete (for table cell save)
    onCommit?.();
  };

  const handleAddLabel = () => {
    setShowLabel(true);
    // Focus label input and set initial width after state update
    setTimeout(() => {
      if (labelInputRef.current) {
        const input = labelInputRef.current;

        // Set initial small width for placeholder
        const span = document.createElement('span');
        span.style.visibility = 'hidden';
        span.style.position = 'absolute';
        span.style.whiteSpace = 'pre';
        span.style.font = window.getComputedStyle(input).font;
        span.style.fontSize = window.getComputedStyle(input).fontSize;
        span.style.fontWeight = window.getComputedStyle(input).fontWeight;
        span.textContent = input.placeholder;
        document.body.appendChild(span);

        const textWidth = span.offsetWidth;
        document.body.removeChild(span);

        // Add padding
        const padding = 28;
        const minWidth = 20;
        const newWidth = Math.max(minWidth, textWidth + padding);

        input.style.width = `${newWidth}px`;
        input.focus();
      }
    }, 0);
  };


  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setLocalContent(newContent);
    setCharCount(newContent.length);

    // Validate
    const validation = validateSecretField(localLabel, newContent);
    setError(validation.valid ? null : validation.error || null);

    if (validation.valid) {
      // Always write plaintext to the node. Encryption happens at export
      // time in SecretFieldVisitor, not here.
      onUpdate(localLabel, newContent);
    }
  };

  const handleContentAreaClick = () => {
    if (isLocked) return; // Cannot edit without the note key
    if (!isEditingContent) {
      setIsEditingContent(true);
      setTimeout(() => {
        contentTextareaRef.current?.focus();
      }, 0);
    }
  };

  const handleContentBlur = () => {
    // Keep editing mode if content is empty to encourage user to add content
    if (localContent.trim()) {
      setIsEditingContent(false);
      // Notify parent that editing is complete (for table cell save)
      onCommit?.();
    }
  };

  const handleCopy = async () => {
    const success = await copyToClipboard(localContent);
    if (success) {
      setCopySuccess(true);
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // Prevent Enter key in label inputs (don't allow line breaks)
    if (e.key === 'Enter' && e.currentTarget.tagName === 'INPUT') {
      e.preventDefault();
      // Optionally blur the input to finish editing
      e.currentTarget.blur();
    }
  };

  const isOverLimit = charCount > MAX_SECRET_CONTENT_LENGTH;

  // Generate masked content like SecretFieldView
  const maskedContent = localContent.length > 0
    ? '*'.repeat(Math.min(localContent.length, 12))
    : '***';

  const lockedPlaceholder = decryptionFailed
    ? '⚠ Decryption failed'
    : '🔒 Unlock to view';

  // If no label, render in single line (compact mode)
  const hasLabel = localLabel.trim().length > 0;

  return (
    <div
      className={cx('secret-field-editor', `secret-field-editor--${type}`, {
        'secret-field-editor--editing': isEditingContent,
        'secret-field-editor--error': error,
        'secret-field-editor--compact': !hasLabel,
      })}
      contentEditable={false}
    >
      {hasLabel ? (
        // Normal mode: Label and content on same line
        <div className={cx('secret-field-row')}>
          {/* Lock Icon */}
          <svg
            className={cx('secret-icon')}
            width={10}
            height={10}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M12 17a2 2 0 0 0 2-2 2 2 0 0 0-2-2 2 2 0 0 0-2 2 2 2 0 0 0 2 2m6-9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h1V6a5 5 0 0 1 5-5 5 5 0 0 1 5 5v2h1m-6-5a3 3 0 0 0-3 3v2h6V6a3 3 0 0 0-3-3z" />
          </svg>

          {/* Label Input */}
          <input
            ref={labelInputRef}
            type="text"
            value={localLabel}
            onChange={handleLabelChange}
            onBlur={handleLabelBlur}
            onKeyDown={handleKeyDown}
            placeholder="Field label (optional)"
            className={cx('label-input')}
            maxLength={100}
          />

          {/* Content (multiline, click to edit) */}
          <div
            className={cx('secret-field-content-inline', {
              'secret-field-content--editable': !isEditingContent,
            })}
            onClick={handleContentAreaClick}
          >
            {isEditingContent ? (
              <>
                <textarea
                  ref={contentTextareaRef}
                  value={localContent}
                  onChange={handleContentChange}
                  onKeyDown={handleKeyDown}
                  onBlur={handleContentBlur}
                  placeholder="Click to add secret content..."
                  className={cx('content-textarea-inline', {
                    'content-textarea--over-limit': isOverLimit,
                    'content-textarea--scrollable': isScrollable,
                  })}
                  maxLength={MAX_SECRET_CONTENT_LENGTH}
                />
                {/* Character Counter - inline */}
                {charCount > 0 && (
                  <span className={cx('char-counter-inline', {
                    'char-counter--over-limit': isOverLimit,
                  })}>
                    {charCount}/{MAX_SECRET_CONTENT_LENGTH}
                  </span>
                )}
              </>
            ) : (
              <span className={cx('content-masked', {
                'content-preview--empty': !localContent.trim(),
                'content-preview--locked': isLocked,
              })}>
                {isLocked ? (
                  <button
                    type="button"
                    onClick={handleUnlock}
                    className={cx('unlock-button')}
                    title="Click to unlock"
                  >
                    {lockedPlaceholder}
                  </button>
                ) : localContent.trim() ? maskedContent : 'Click to add secret content...'}
              </span>
            )}
          </div>

          {/* Buttons - absolute positioned */}
          <div className={cx('secret-field-buttons')}>
            {/* Edit Button - only show when not editing and not locked */}
            {!isEditingContent && !isLocked && (
              <button
                type="button"
                onClick={handleContentAreaClick}
                className={cx('icon-button-compact', 'button-hover-only')}
                title="Edit value"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
                </svg>
              </button>
            )}

            {/* Copy Button */}
            {localContent.trim() && (
              <button
                type="button"
                onClick={handleCopy}
                onMouseDown={(e) => e.preventDefault()}
                className={cx('icon-button-compact', 'copy-button', 'button-hover-only', {
                  'copy-button--success': copySuccess,
                })}
                title={copySuccess ? 'Copied!' : 'Copy to clipboard'}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  {copySuccess ? (
                    // Check icon
                    <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
                  ) : (
                    // Copy icon
                    <path d="M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z" />
                  )}
                </svg>
              </button>
            )}

            {/* Delete Button */}
            <button
              type="button"
              onClick={onDelete}
              className={cx('icon-button-compact', 'delete-button', 'button-hover-only')}
              title="Delete secret field"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        // Compact mode: No label by default, button to add label
        <div className={cx('secret-field-compact-row')}>
          {/* Lock Icon */}
          <svg
            className={cx('secret-icon')}
            width={10}
            height={10}
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M12 17a2 2 0 0 0 2-2 2 2 0 0 0-2-2 2 2 0 0 0-2 2 2 2 0 0 0 2 2m6-9a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V10a2 2 0 0 1 2-2h1V6a5 5 0 0 1 5-5 5 5 0 0 1 5 5v2h1m-6-5a3 3 0 0 0-3 3v2h6V6a3 3 0 0 0-3-3z" />
          </svg>

          {/* Show label input if label is being edited */}
          {showLabel && (
            <input
              ref={labelInputRef}
              type="text"
              value={localLabel}
              onChange={handleLabelChange}
              onBlur={handleLabelBlur}
              onKeyDown={handleKeyDown}
              placeholder="label"
              className={cx('label-input-compact')}
              maxLength={100}
            />
          )}

          {/* Content (multiline, click to edit) */}
          <div
            className={cx('secret-field-content-compact', {
              'secret-field-content--editable': !isEditingContent,
            })}
            onClick={handleContentAreaClick}
          >
            {isEditingContent ? (
              <textarea
                ref={contentTextareaRef}
                value={localContent}
                onChange={handleContentChange}
                onKeyDown={handleKeyDown}
                onBlur={handleContentBlur}
                placeholder="secret content..."
                className={cx('content-textarea-compact', {
                  'content-textarea--over-limit': isOverLimit,
                  'content-textarea--scrollable': isScrollable,
                })}
                maxLength={MAX_SECRET_CONTENT_LENGTH}
              />
            ) : (
              <span className={cx('content-masked-compact', {
                'content-preview--empty': !localContent.trim(),
                'content-preview--locked': isLocked,
              })}>
                {isLocked ? (
                  <button
                    type="button"
                    onClick={handleUnlock}
                    className={cx('unlock-button')}
                    title="Click to unlock"
                  >
                    {lockedPlaceholder}
                  </button>
                ) : localContent.trim() ? maskedContent : 'secret content...'}
              </span>
            )}
          </div>

          {/* Buttons - absolute positioned */}
          <div className={cx('secret-field-buttons')}>
            {/* Edit Button - only show when not editing and not locked */}
            {!isEditingContent && !isLocked && (
              <button
                type="button"
                onClick={handleContentAreaClick}
                className={cx('icon-button-compact', 'button-hover-only')}
                title="Edit value"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20.71,7.04C21.1,6.65 21.1,6 20.71,5.63L18.37,3.29C18,2.9 17.35,2.9 16.96,3.29L15.12,5.12L18.87,8.87M3,17.25V21H6.75L17.81,9.93L14.06,6.18L3,17.25Z" />
                </svg>
              </button>
            )}

            {/* Add Label Button - only show when no label */}
            {!showLabel && (
              <button
                type="button"
                onClick={handleAddLabel}
                className={cx('icon-button-compact', 'add-label-button-compact', 'button-hover-only')}
                title="Add label"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
                </svg>
              </button>
            )}

            {/* Copy Button */}
            {localContent.trim() && (
              <button
                type="button"
                onClick={handleCopy}
                onMouseDown={(e) => e.preventDefault()}
                className={cx('icon-button-compact', 'copy-button', 'button-hover-only', {
                  'copy-button--success': copySuccess,
                })}
                title={copySuccess ? 'Copied!' : 'Copy to clipboard'}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  {copySuccess ? (
                    // Check icon
                    <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
                  ) : (
                    // Copy icon
                    <path d="M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z" />
                  )}
                </svg>
              </button>
            )}

            {/* Delete Button */}
            <button
              type="button"
              onClick={onDelete}
              className={cx('icon-button-compact', 'delete-button', 'button-hover-only')}
              title="Delete secret field"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19,4H15.5L14.5,3H9.5L8.5,4H5V6H19M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19Z" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className={cx('error-message')}>
          {error}
        </div>
      )}
    </div>
  );
};

export default SecretFieldEditor;

